(() => {
  if (customElements.get('shop-catalog')) return;
  customElements.define('shop-catalog', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const { signal } = this.events;
      this.results = this.querySelector('.catalog-results');
      this.sort = this.querySelector('[data-sort]');
      this.setupSortPopup(signal);
      this.categories = [...this.querySelectorAll('[data-category]')];
      this.mobileLayout = matchMedia('(max-width: 740px)');
      this.desktopDefaultColumns = this.dataset.columns || '3';
      this.state = { collection: this.dataset.defaultCollection, sort: this.dataset.defaultSort || 'manual', page: 1 };
      this.committed = { ...this.state };
      this.restoreColumns();
      this.mobileLayout.addEventListener('change', () => this.restoreColumns(), { signal });
      this.addEventListener('click', event => {
        const category = event.target.closest('[data-category]');
        const pagination = event.target.closest('[data-page]');
        const columns = event.target.closest('[data-columns-button]');
        if ((event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) && (category || pagination)) return;
        if (category) {
          event.preventDefault();
          this.load({ ...this.state, collection: category.dataset.category, page: 1 });
        } else if (pagination) {
          event.preventDefault();
          this.load({ ...this.state, page: Number(pagination.dataset.page) }, true);
        } else if (columns) {
          this.setColumns(columns.dataset.columnsButton);
        } else if (event.target.closest('[data-retry]')) {
          this.load(this.failedState || this.state);
        }
      }, { signal });
      this.sort.addEventListener('change', () => this.load({ ...this.state, sort: this.sort.value, page: 1 }), { signal });
      window.addEventListener('popstate', () => this.load(this.readUrl(), false, false), { signal });
      this.syncControls();
      const requested = this.readUrl();
      if (JSON.stringify(requested) !== JSON.stringify(this.state)) {
        this.load(requested, false, false);
      } else {
        // The server-rendered collection already matches the requested URL.
        // Reveal it instead of leaving the initial loading guard in place.
        this.removeAttribute('data-initial-loading');
      }
      this.rewritePagination();
    }

    setColumns(value) {
      const allowedColumns = this.mobileLayout?.matches ? ['1', '2'] : ['2', '3'];
      if (!allowedColumns.includes(value)) return;
      this.dataset.columns = value;
      this.querySelectorAll('[data-columns-button]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.columnsButton === value)));
      try { localStorage.setItem(this.columnsStorageKey(), value); } catch (_) {}
    }

    columnsStorageKey() {
      return this.mobileLayout?.matches ? 'krbobo-catalog-columns-mobile-v1' : 'krbobo-catalog-columns-v2';
    }

    restoreColumns() {
      const defaultColumns = this.mobileLayout?.matches ? '2' : this.desktopDefaultColumns;
      try { this.setColumns(localStorage.getItem(this.columnsStorageKey()) || defaultColumns); } catch (_) { this.setColumns(defaultColumns); }
    }

    setupSortPopup(signal) {
      const wrapper = this.sort.closest('.catalog-sort');
      wrapper.querySelector('.catalog-sort-trigger')?.remove();
      wrapper.querySelector('.catalog-sort-popup')?.remove();
      this.sortTrigger = document.createElement('button');
      this.sortTrigger.type = 'button';
      this.sortTrigger.className = 'catalog-sort-trigger';
      this.sortTrigger.setAttribute('aria-haspopup', 'menu');
      this.sortTrigger.setAttribute('aria-expanded', 'false');
      this.sortTrigger.innerHTML = '<span></span><svg viewBox="0 0 12 8" aria-hidden="true"><path d="m1 1 5 5 5-5"/></svg>';
      this.sortPopup = document.createElement('div');
      this.sortPopup.className = 'catalog-sort-popup';
      this.sortPopup.hidden = true;
      this.sortPopup.innerHTML = '<div class="catalog-sort-popup__top"><button type="button" class="catalog-sort-close" aria-label="Close sort options">−</button></div><div class="catalog-sort-options" role="menu" aria-label="Sort products"></div>';
      const list = this.sortPopup.querySelector('[role="menu"]');
      this.sortButtons = [...this.sort.options].map(option => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'catalog-sort-option';
        button.textContent = option.textContent;
        button.dataset.sortValue = option.value;
        button.setAttribute('role', 'menuitemradio');
        button.tabIndex = -1;
        button.addEventListener('click', () => {
          this.sort.value = option.value;
          this.closeSortPopup(true);
          this.sort.dispatchEvent(new Event('change', { bubbles: true }));
        }, { signal });
        list.append(button);
        return button;
      });
      wrapper.append(this.sortTrigger, this.sortPopup);
      this.sort.hidden = true;
      this.sortTrigger.addEventListener('click', () => {
        if (this.sortPopup.hidden) this.openSortPopup();
        else this.closeSortPopup();
      }, { signal });
      this.sortTrigger.addEventListener('keydown', event => {
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        event.preventDefault();
        this.openSortPopup(event.key === 'ArrowUp');
      }, { signal });
      this.sortPopup.querySelector('.catalog-sort-close').addEventListener('click', () => this.closeSortPopup(true), { signal });
      wrapper.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !this.sortPopup.hidden) {
          event.preventDefault();
          this.closeSortPopup(true);
        }
        const index = this.sortButtons.indexOf(document.activeElement);
        if (index < 0) return;
        let next;
        if (event.key === 'ArrowDown') next = (index + 1) % this.sortButtons.length;
        if (event.key === 'ArrowUp') next = (index - 1 + this.sortButtons.length) % this.sortButtons.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.sortButtons.length - 1;
        if (next !== undefined) {
          event.preventDefault();
          this.sortButtons[next].focus();
        }
      }, { signal });
      wrapper.addEventListener('focusout', event => {
        if (!wrapper.contains(event.relatedTarget)) this.closeSortPopup();
      }, { signal });
      document.addEventListener('pointerdown', event => {
        if (!wrapper.contains(event.target)) this.closeSortPopup();
      }, { signal });
    }

    openSortPopup(last = false) {
      const rect = this.sortTrigger.getBoundingClientRect();
      const below = innerHeight - rect.bottom - 16;
      const above = rect.top - 16;
      const upward = below < 220 && above > below;
      this.sortPopup.classList.toggle('is-above', upward);
      this.sortPopup.style.setProperty('--sort-height', `${Math.max(120, Math.min(380, upward ? above : below))}px`);
      this.sortPopup.hidden = false;
      this.sortTrigger.setAttribute('aria-expanded', 'true');
      const selected = this.sortButtons.find(button => button.dataset.sortValue === this.sort.value);
      (last ? this.sortButtons.at(-1) : selected || this.sortButtons[0]).focus({ preventScroll: true });
    }

    closeSortPopup(restoreFocus = false) {
      this.sortPopup.hidden = true;
      this.sortTrigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this.sortTrigger.focus({ preventScroll: true });
    }

    readUrl() {
      const params = new URL(location.href).searchParams;
      const requestedCollection = params.get('category');
      // Support menu URLs created before the catalog FEATURED handle was
      // standardized as "new-arrivals".
      const categoryAliases = { featured: 'new-arrivals' };
      const collection = categoryAliases[requestedCollection] || requestedCollection;
      const sort = params.get('sort_by');
      const allowedSorts = [...this.sort.options].map(option => option.value);
      const defaultSort = allowedSorts.includes(this.dataset.defaultSort) ? this.dataset.defaultSort : 'manual';
      return {
        collection: this.categories.some(item => item.dataset.category === collection) ? collection : this.dataset.defaultCollection,
        sort: allowedSorts.includes(sort) ? sort : defaultSort,
        page: Math.max(1, parseInt(params.get('page'), 10) || 1)
      };
    }

    pageUrl(state) {
      const url = new URL(location.href);
      url.searchParams.set('category', state.collection);
      url.searchParams.set('sort_by', state.sort);
      if (state.page > 1) url.searchParams.set('page', state.page);
      else url.searchParams.delete('page');
      return url;
    }

    syncControls() {
      this.categories.forEach(item => {
        if (item.dataset.category === this.state.collection) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });
      this.sort.value = this.state.sort;
      const label = this.sort.selectedOptions[0]?.textContent || 'featured';
      this.sortTrigger.querySelector('span').textContent = label;
      this.sortTrigger.setAttribute('aria-label', `Sort: ${label}`);
      this.sortButtons.forEach(button => button.setAttribute('aria-checked', String(button.dataset.sortValue === this.state.sort)));
    }

    rewritePagination() {
      this.results.querySelectorAll('[data-page]').forEach(link => {
        link.href = this.pageUrl({ ...this.committed, page: Number(link.dataset.page) }).href;
      });
    }

    async load(next, scroll = false, push = true) {
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      this.state = { ...next };
      this.syncControls();
      this.results.setAttribute('aria-busy', 'true');
      this.querySelector('.catalog-error').hidden = true;
      try {
        const category = this.categories.find(item => item.dataset.category === next.collection);
        const endpoint = new URL(category.href, location.origin);
        endpoint.searchParams.set('view', 'kr-catalog');
        endpoint.searchParams.set('sort_by', next.sort);
        endpoint.searchParams.set('page', next.page);
        // Keep unpublished-theme previews on the same theme during AJAX requests.
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) endpoint.searchParams.set('preview_theme_id', preview);
        const response = await fetch(endpoint, { signal: request.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = doc.querySelector('[data-catalog-results]');
        if (!content || content.dataset.collection !== next.collection) throw new Error('Invalid collection response');
        if (request.signal.aborted) return;
        this.results.replaceChildren(content);
        this.removeAttribute('data-initial-loading');
        this.querySelector('[data-count-label]').textContent = `${content.dataset.count} products`;
        this.committed = { ...next };
        this.failedState = null;
        this.rewritePagination();
        if (push) history.pushState(null, '', this.pageUrl(next));
        if (scroll) this.querySelector('.catalog-toolbar').scrollIntoView({ block: 'start', behavior: 'instant' });
      } catch (error) {
        if (request.signal.aborted) return;
        this.failedState = { ...next };
        this.state = { ...this.committed };
        this.syncControls();
        this.querySelector('.catalog-error').hidden = false;
      } finally {
        if (this.request === request) this.results.setAttribute('aria-busy', 'false');
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
    }
  });
})();
